import React, { useEffect, useRef, useState } from 'react'
import {
  Grid,
  Typography,
  Box,
  useTheme,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableCell, TableBody, TableRow, styled,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import SendIcon from '@mui/icons-material/Send'
import { makeStyles } from '@mui/styles'
import { useHistory } from 'react-router-dom'
import Env from '../../shared/config/envConfig'
import { useSelector } from 'react-redux'
import { getUser } from '../../store/settings/settings.selectors'
import { addCsrfHeaders } from '../../shared/config/csrf'
import CardComponent from './CardComponent'
import formatCurrency from '@3m5/crude-frontend/dist/shared/util/formatCurrency'
import formatDate from '@3m5/crude-frontend/dist/shared/util/formatDate'

import PersonIcon from '@mui/icons-material/Person'
import GroupIcon from '@mui/icons-material/Group'
import GroupsIcon from '@mui/icons-material/Groups'

const useStyles = makeStyles(() => ({
  root: {
    marginTop: '40px',
  },
  actionButton: {
    marginLeft: '8px',
  },
}))

interface UnconfirmedRegistration {
  participationId: string
  eventId: string
  eventName: string
  contactName: string
  contactEmail: string
  registrationDate: string | null
}

const Dashboard: React.FC = () => {
  const classes = useStyles()
  const user = useSelector(getUser)
  const theme = useTheme()
  const history = useHistory()

  const [mobileLayout, setMobileLayout] = useState(false)
  const [statisticData, setStatisticData] = useState<any>()
  const [cardWidth, setCardWidth] = useState<number>(12)
  const [snackOpen, setSnackOpen] = useState(false)
  const [snackMsg, setSnackMsg] = useState('')
  const [snackSeverity, setSnackSeverity] = useState<'success' | 'error'>('success')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const rootRef = useRef<HTMLElement>(null)

  const StyledTableRow = styled(TableRow)(() => ({
    '&:last-child td, &:last-child th': {
      border: 0,
    },
    [theme.breakpoints.down('md')]: {
      '& td, th': {
        border: 0,
      },
      '&:first-of-type': {
        borderTop: `1px solid ${theme.palette.divider}`,
      },
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  }))

  useEffect(() => {
    if (rootRef.current) {
      const element: HTMLElement = rootRef.current

      setMobileLayout(element.offsetWidth < 600)

      const observer = new ResizeObserver(() => {
        setMobileLayout(element.offsetWidth < 600)
      })
      observer.observe(element)

      return () => {
        observer.disconnect(element)
      }
    }
  }, [rootRef, setMobileLayout])

  const fetchDashboardData = () => {
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')
    addCsrfHeaders(requestHeaders)
    const restUrl = `${Env.basePath}/api/dashboard`
    fetch(restUrl,
      {
        method: 'GET',
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(res => res.json())
      .then(json => {
        setStatisticData(json)
        if (json.membershipCounts.length > 0) {
          setCardWidth(12 / json.membershipCounts.length)
        } else {
          setCardWidth(12)
        }
      })
      .catch(err => console.log(err))
  }

  useEffect(() => {
    fetchDashboardData()
  }, [],
  )

  const handleAction = (endpoint: string, participationId: string, successMsg: string) => {
    if (endpoint === 'delete-registration' && !deleteDialogOpen) {
      setPendingDeleteId(participationId)
      setDeleteDialogOpen(true)
      return
    }
    setDeleteDialogOpen(false)
    setActionLoading(participationId)
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')
    addCsrfHeaders(requestHeaders)

    fetch(`${Env.basePath}/api/public/events/${endpoint}?participationId=${participationId}`, {
      method: 'POST',
      headers: requestHeaders,
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.errorMessage || data.error || 'Aktion fehlgeschlagen')
        }
        setSnackMsg(successMsg)
        setSnackSeverity('success')
        setSnackOpen(true)
        fetchDashboardData()
      })
      .catch((err) => {
        console.error(`Error during ${endpoint}:`, err)
        setSnackMsg(err.message || 'Ein Fehler ist aufgetreten.')
        setSnackSeverity('error')
        setSnackOpen(true)
      })
      .finally(() => setActionLoading(null))
  }

  /* const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0])
    }
  }

  const uploadFile = async () => {
    if (!selectedFile) {
      return
    }

    const formData = new FormData()
    formData.append('file', selectedFile)

    const uploadHeaders: HeadersInit = new Headers()
    // CSRF-Header hinzufügen
    addCsrfHeaders(uploadHeaders)

    try {
      const response = await fetch(`${Env.basePath}/api/import/excel`, {
        method: 'POST',
        credentials: 'include',
        headers: uploadHeaders,
        body: formData,
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Datei erfolgreich hochgeladen:', data)
        // Hier können Sie weitere Aktionen nach erfolgreichem Upload durchführen
      } else {
        console.error('Fehler beim Hochladen der Datei:', await response.text())
      }
    } catch (error) {
      console.error('Fehler bei der Anfrage:', error)
    }
  } */

  const handleInvoiceClick = (invoice: any) => {
    const id = invoice.invoiceId || invoice.id
    if (!id) return

    if (invoice.invoiceType === 'EVENT') {
      history.push(`/event-invoice/${id}`)
    } else {
      history.push(`/invoice/${id}`)
    }
  }

  return (
    <>
      <Grid ref={rootRef} container className={classes.root}>
        <Grid item xs={12} container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ backgroundColor: theme.palette.primary.main, borderRadius: '10px', px: mobileLayout ? 2 : 4, py: mobileLayout ? 2 : 5 }}>
              <Typography variant='h4' sx={{ color: theme.palette.primary.contrastText }}>{`Hallo ${user?.userName}`}</Typography>
              <Typography variant='h6' sx={{ color: theme.palette.primary.contrastText }}>Willkommen zurück im Admin-Bereich.</Typography>
            </Box>
          </Grid>
          {/*
          <div style={{ margin: '24px' }}>
            <input
              style={{ display: 'none' }}
              id='file-upload-button'
              type='file'
              onChange={handleFileChange}
            />
            <label htmlFor='file-upload-button'>
              <Button
                variant='contained'
                component='span'
                color='primary'
              >
                Datei auswählen
              </Button>
            </label>
            {selectedFile && (
              <>
                <span style={{ marginLeft: '10px' }}>
                  {selectedFile.name}
                </span>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={uploadFile}
                  style={{ marginLeft: '10px' }}
                >
                  Hochladen
                </Button>
              </>
            )}
          </div>
        */}
          <Grid item xs={12} container spacing={3} direction={mobileLayout ? 'column-reverse' : 'row'}>
            {statisticData?.membershipCounts.map((elem: any, index: number) =>
              <Grid item xs={cardWidth} key={index}>
                <CardComponent
                  height={mobileLayout ? 170 : 240}
                >
                  <Stack alignItems='center' justifyContent='center' height='100%'>
                    <Box
                      sx={{ position: 'relative' }}
                    >
                      {elem.contactCount === 1 &&
                        <PersonIcon sx={{ width: '40px', height: '40px' }} />}
                      {elem.contactCount === 2 &&
                        <GroupIcon sx={{ width: '40px', height: '40px' }} />}
                      {elem.contactCount !== 1 && elem.contactCount !== 2 &&
                        <GroupsIcon sx={{ width: '40px', height: '40px' }} />}
                    </Box>
                    <Typography variant='h6' textAlign='center'>{elem.membershipTypeName}</Typography>
                    <Typography variant='h4'><b>{elem.count}</b></Typography>
                  </Stack>
                </CardComponent>
              </Grid>
            )}
            <Grid item xs={mobileLayout ? undefined : 6}>
              <CardComponent padding={mobileLayout ? 0 : undefined} height={mobileLayout ? 'unset' : 350} sx={{ overflow: mobileLayout ? 'hidden' : 'scroll' }}>
                <Stack spacing={1}>
                  <Typography sx={{ padding: mobileLayout ? 2 : 0, paddingBottom: 0 }} variant='h5'>Jahreseinnahmen</Typography>
                  <TableContainer>
                    <Table>
                      {!mobileLayout &&
                        <TableHead>
                          <StyledTableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Einnahmetyp</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>in Rechnung gestellt</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>erhalten</TableCell>
                          </StyledTableRow>
                        </TableHead>}
                      <TableBody>
                        {statisticData?.currentYearRevenueByType && (
                          <>
                            {statisticData?.currentYearRevenueByType.map((elem: any, index: number) => (
                              <StyledTableRow
                                sx={{ cursor: 'pointer' }}
                                key={index}
                              >
                                {mobileLayout ? (
                                  <TableCell>
                                    <Grid container spacing={1}>
                                      <Grid item xs={6}>Einnahmetyp</Grid>
                                      <Grid item xs={6}><Typography>{elem.invoiceType}</Typography></Grid>
                                      <Grid item xs={6}>in Rechnung gestellt</Grid>
                                      <Grid item xs={6}><Typography>{formatCurrency(elem.issuedTotal)}</Typography></Grid>
                                      <Grid item xs={6}>erhalten</Grid>
                                      <Grid item xs={6}><Typography>{formatCurrency(elem.paidTotal)}</Typography></Grid>
                                    </Grid>
                                  </TableCell>)
                                  : (
                                    <>
                                      <TableCell component='th' scope='row'>
                                        {elem.invoiceType}
                                      </TableCell>
                                      <TableCell sx={{ textAlign: 'center' }}>{formatCurrency(elem.issuedTotal)}</TableCell>
                                      <TableCell sx={{ textAlign: 'center' }}>{formatCurrency(elem.paidTotal)}</TableCell>
                                    </>)}
                              </StyledTableRow>
                            ))}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardComponent>
            </Grid>
            <Grid item xs={mobileLayout ? undefined : 6}>
              <CardComponent padding={mobileLayout ? 0 : undefined} height={mobileLayout ? 'unset' : 350} sx={{ overflow: mobileLayout ? 'hidden' : 'scroll' }}>
                <Stack spacing={1}>
                  <Typography sx={{ padding: mobileLayout ? 2 : 0, paddingBottom: 0 }} variant='h5'>Offene Rechnungen</Typography>
                  <TableContainer>
                    <Table>
                      {!mobileLayout &&
                        <TableHead>
                          <StyledTableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Rechnungsdatum</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Betrag</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Firma</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Rechnungstyp</TableCell>
                          </StyledTableRow>
                        </TableHead>}
                      <TableBody>
                        {statisticData && statisticData?.openInvoices.length > 0
                          ? (
                            <>
                              {statisticData?.openInvoices.map((invoice: any, index: number) => (
                                <StyledTableRow
                                  sx={{ cursor: 'pointer' }}
                                  key={index}
                                  onClick={() => handleInvoiceClick(invoice)}
                                >
                                  {mobileLayout ? (
                                    <TableCell>
                                      <Grid container spacing={1}>
                                        <Grid item xs={6}>Rechnungsdatum</Grid>
                                        <Grid item xs={6}><Typography>{formatDate(new Date(invoice.invoiceDate).getTime() / 1000)}</Typography></Grid>
                                        <Grid item xs={6}>Betrag</Grid>
                                        <Grid item xs={6}><Typography>{formatCurrency(invoice.totalAmount)}</Typography></Grid>
                                        <Grid item xs={6}>Firma</Grid>
                                        <Grid item xs={6}><Typography>{invoice.billingAddress}</Typography></Grid>
                                        <Grid item xs={6}>Rechnungstyp</Grid>
                                        <Grid item xs={6}><Typography>{invoice.invoiceType}</Typography></Grid>
                                      </Grid>
                                    </TableCell>)
                                    : (
                                      <>
                                        <TableCell component='th' scope='row'>
                                          {formatDate(new Date(invoice.invoiceDate).getTime() / 1000)}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{formatCurrency(invoice.totalAmount)}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{invoice.billingAddress}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{invoice.invoiceType}</TableCell>
                                      </>)}
                                </StyledTableRow>
                              ))}
                            </>
                          )
                          : (
                            <TableCell colSpan={4} sx={{ width: '100%', textAlign: 'center' }}>
                              <Typography variant='body1'>Alle Rechnungen sind vollständig beglichen.</Typography>
                            </TableCell>)}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CardComponent>
            </Grid>
            <Grid item xs={12}>
              <CardComponent padding={mobileLayout ? 0 : undefined} height={mobileLayout ? 'unset' : 350} sx={{ overflow: mobileLayout ? 'hidden' : 'scroll' }}>
                <Stack spacing={1}>
                  <Typography sx={{ padding: mobileLayout ? 2 : 0, paddingBottom: 0 }} variant='h5'>{statisticData?.upcomingEvent?.eventName}</Typography>
                  <Typography sx={{ padding: mobileLayout ? 2 : 0, paddingBottom: 0 }} variant='body2'>{`${formatDate(new Date(statisticData?.upcomingEvent?.dateFrom).getTime() / 1000)} - ${formatDate(new Date(statisticData?.upcomingEvent?.dateUntil).getTime() / 1000)}`}</Typography>
                  <Grid container item xs={12}>
                    <Grid item xs={mobileLayout ? 12 : 4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: mobileLayout ? 2 : 0, paddingBottom: 0 }}>
                        <Typography variant='h6'>Anmeldungen</Typography>
                        {statisticData?.registrationsByEvent.find((elem: any) => elem.eventId === statisticData?.upcomingEvent?.eventId) && (
                          <Typography variant='h6' sx={{ ml: 1 }}>
                            ({statisticData?.registrationsByEvent.find((elem: any) => elem.eventId === statisticData?.upcomingEvent?.eventId).byEventCost.reduce((sum: number, opt: any) => sum + opt.count, 0)})
                          </Typography>
                        )}
                      </Box>
                      <TableContainer>
                        <Table>
                          {!mobileLayout &&
                            <TableHead>
                              <StyledTableRow>
                                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Registrierung</TableCell>
                                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Anzahl</TableCell>
                              </StyledTableRow>
                            </TableHead>}
                          <TableBody>
                            {statisticData && statisticData?.registrationsByEvent.find((elem: any) => elem.eventId === statisticData?.upcomingEvent?.eventId) != null
                              ? (
                                <>
                                  {statisticData?.registrationsByEvent.find((elem: any) => elem.eventId === statisticData?.upcomingEvent?.eventId).byEventCost.map((option: any, index: number) => (
                                    <StyledTableRow
                                      sx={{ cursor: 'pointer' }}
                                      key={index}
                                    >
                                      {mobileLayout ? (
                                        <TableCell>
                                          <Grid container spacing={1}>
                                            <Grid item xs={6}>Registrierung</Grid>
                                            <Grid item xs={6}><Typography>{option.optionName}</Typography></Grid>
                                            <Grid item xs={6}>Anzahl</Grid>
                                            <Grid item xs={6}><Typography>{option.count}</Typography></Grid>
                                          </Grid>
                                        </TableCell>)
                                        : (
                                          <>
                                            <TableCell sx={{ textAlign: 'center' }}>{option.optionName}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>{option.count}</TableCell>
                                          </>)}
                                    </StyledTableRow>
                                  ))}
                                </>
                              )
                              : (
                                <TableCell colSpan={4} sx={{ width: '100%', textAlign: 'center' }}>
                                  <Typography variant='body1'>Es liegen noch keine Registrierungen vor.</Typography>
                                </TableCell>)}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={mobileLayout ? 12 : 4}>
                      <Typography sx={{ padding: mobileLayout ? 2 : 0, paddingBottom: 0, justifyContent: 'center', width: '100%', display: 'flex' }} variant='h6'>Essenspräferenz </Typography>
                      <TableContainer>
                        <Table>
                          {!mobileLayout &&
                            <TableHead>
                              <StyledTableRow>
                                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Präferenz</TableCell>
                                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Anzahl</TableCell>
                              </StyledTableRow>
                            </TableHead>}
                          <TableBody>
                            {statisticData && statisticData?.registrationsByEvent.find((elem: any) => elem.eventId === statisticData?.upcomingEvent?.eventId) != null
                              ? (
                                <>
                                  {statisticData?.registrationsByEvent.find((elem: any) => elem.eventId === statisticData?.upcomingEvent?.eventId).byDiet.map((option: any, index: number) => (
                                    <StyledTableRow
                                      sx={{ cursor: 'pointer' }}
                                      key={index}
                                    >
                                      {mobileLayout ? (
                                        <TableCell>
                                          <Grid container spacing={1}>
                                            <Grid item xs={6}>Präferenz</Grid>
                                            <Grid item xs={6}><Typography>{option.dietName}</Typography></Grid>
                                            <Grid item xs={6}>Anzahl</Grid>
                                            <Grid item xs={6}><Typography>{option.count}</Typography></Grid>
                                          </Grid>
                                        </TableCell>)
                                        : (
                                          <>
                                            <TableCell sx={{ textAlign: 'center' }}>{option.dietName}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>{option.count}</TableCell>
                                          </>)}
                                    </StyledTableRow>
                                  ))}
                                </>
                              )
                              : (
                                <TableCell colSpan={4} sx={{ width: '100%', textAlign: 'center' }}>
                                  <Typography variant='body1'>Es liegen noch keine Registrierungen vor.</Typography>
                                </TableCell>)}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={mobileLayout ? 12 : 4}>
                      <Typography sx={{ padding: mobileLayout ? 2 : 0, paddingBottom: 0, justifyContent: 'center', width: '100%', display: 'flex' }} variant='h6'>Veranstaltung </Typography>
                      <TableContainer>
                        <Table>
                          {!mobileLayout &&
                            <TableHead>
                              <StyledTableRow>
                                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Veranstaltung</TableCell>
                                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Anzahl</TableCell>
                              </StyledTableRow>
                            </TableHead>}
                          <TableBody>
                            {statisticData && statisticData?.registrationsBySessionForUpcomingEvent.length !== 0
                              ? (
                                <>
                                  {statisticData?.registrationsBySessionForUpcomingEvent.map((event: any, index: number) => (
                                    <StyledTableRow
                                      sx={{ cursor: 'pointer' }}
                                      key={index}
                                    >
                                      {mobileLayout ? (
                                        <TableCell>
                                          <Grid container spacing={1}>
                                            <Grid item xs={6}>Veranstaltung</Grid>
                                            <Grid item xs={6}><Typography>{event.sessionName}</Typography></Grid>
                                            <Grid item xs={6}>Anzahl</Grid>
                                            <Grid item xs={6}><Typography>{event.registrations}</Typography></Grid>
                                          </Grid>
                                        </TableCell>)
                                        : (
                                          <>
                                            <TableCell sx={{ textAlign: 'center' }}>{event.sessionName}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>{event.registrations}</TableCell>
                                          </>)}
                                    </StyledTableRow>
                                  ))}
                                </>
                              )
                              : (
                                <TableCell colSpan={4} sx={{ width: '100%', textAlign: 'center' }}>
                                  <Typography variant='body1'>Es liegen noch keine Registrierungen vor.</Typography>
                                </TableCell>)}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </Stack>
              </CardComponent>
            </Grid>
            {statisticData?.unconfirmedRegistrations && statisticData.unconfirmedRegistrations.length > 0 && (
              <Grid item xs={12}>
                <CardComponent padding={mobileLayout ? 0 : undefined} sx={{ overflow: 'auto' }}>
                  <Stack spacing={1}>
                    <Typography sx={{ padding: mobileLayout ? 2 : 0, paddingBottom: 0 }} variant='h5'>Unbestätigte Registrierungen</Typography>
                    <TableContainer>
                      <Table>
                        {!mobileLayout && (
                          <TableHead>
                            <StyledTableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Event</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>E-Mail</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Datum</TableCell>
                              <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Aktionen</TableCell>
                            </StyledTableRow>
                          </TableHead>
                        )}
                        <TableBody>
                          {statisticData.unconfirmedRegistrations.map((reg: UnconfirmedRegistration, index: number) => (
                            <StyledTableRow key={index}>
                              {mobileLayout ? (
                                <TableCell>
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>Event</Grid>
                                    <Grid item xs={6}><Typography>{reg.eventName}</Typography></Grid>
                                    <Grid item xs={6}>Name</Grid>
                                    <Grid item xs={6}><Typography>{reg.contactName}</Typography></Grid>
                                    <Grid item xs={6}>E-Mail</Grid>
                                    <Grid item xs={6}><Typography>{reg.contactEmail}</Typography></Grid>
                                    <Grid item xs={6}>Datum</Grid>
                                    <Grid item xs={6}><Typography>{reg.registrationDate ? formatDate(new Date(reg.registrationDate).getTime() / 1000) : '-'}</Typography></Grid>
                                    <Grid item xs={12} sx={{ mt: 1, textAlign: 'center' }}>
                                      <Button
                                        size='small'
                                        startIcon={actionLoading === reg.participationId ? <CircularProgress size={16} /> : <SendIcon />}
                                        onClick={() => handleAction('resend-confirmation', reg.participationId, 'Bestätigungsmail wurde erneut versendet.')}
                                        disabled={actionLoading !== null}
                                        sx={{ mr: 1 }}
                                      >
                                        Mail senden
                                      </Button>
                                      <Button
                                        size='small'
                                        color='error'
                                        startIcon={actionLoading === reg.participationId ? <CircularProgress size={16} /> : <DeleteIcon />}
                                        onClick={() => handleAction('delete-registration', reg.participationId, 'Registrierung wurde gelöscht.')}
                                        disabled={actionLoading !== null}
                                      >
                                        Löschen
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </TableCell>
                              ) : (
                                <>
                                  <TableCell>{reg.eventName}</TableCell>
                                  <TableCell>{reg.contactName}</TableCell>
                                  <TableCell>{reg.contactEmail}</TableCell>
                                  <TableCell>{reg.registrationDate ? formatDate(new Date(reg.registrationDate).getTime() / 1000) : '-'}</TableCell>
                                  <TableCell sx={{ textAlign: 'center' }}>
                                    <Tooltip title='Bestätigungsmail erneut senden'>
                                      <IconButton
                                        color='primary'
                                        onClick={() => handleAction('resend-confirmation', reg.participationId, 'Bestätigungsmail wurde erneut versendet.')}
                                        disabled={actionLoading !== null}
                                      >
                                        {actionLoading === reg.participationId ? <CircularProgress size={24} /> : <SendIcon />}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title='Registrierung löschen'>
                                      <IconButton
                                        color='error'
                                        onClick={() => handleAction('delete-registration', reg.participationId, 'Registrierung wurde gelöscht.')}
                                        disabled={actionLoading !== null}
                                      >
                                        {actionLoading === reg.participationId ? <CircularProgress size={24} /> : <DeleteIcon />}
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </>
                              )}
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </CardComponent>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={() => setSnackOpen(false)}>
        <Alert onClose={() => setSnackOpen(false)} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'
      >
        <DialogTitle id='delete-dialog-title'>
          Registrierung löschen?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='delete-dialog-description'>
            Bist du sicher, dass du diese Registrierung unwiderruflich löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color='primary'>
            Abbrechen
          </Button>
          <Button
            onClick={() => pendingDeleteId && handleAction('delete-registration', pendingDeleteId, 'Registrierung wurde gelöscht.')}
            color='error'
            autoFocus
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
export default Dashboard
